/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IPivotStyleProps, IPivotStyles, Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { CluEntityCombinationPivot } from 'src/clu/appShell/cluEntityDetailsPage/components/CluEntityCombinationPivot';
import { CluEntityDetailsPivot } from 'src/clu/appShell/cluEntityDetailsPage/components/CluEntityDetailsPivot';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { EntityCompositionSetting } from 'src/core/types/EntitySwagger';

export type CluEntityDetailsWrapperProps = { entityName: string };

const messages = defineMessages({
    entityOptions: { id: 'clu.cluEntityDetailsWrapper.entityOptions', defaultMessage: 'Entity Options' },
    entityComponents: { id: 'clu.cluEntityDetailsWrapper.entityComponents', defaultMessage: 'Entity components' },
    saveCombinationFailed: { id: 'clu.cluEntityDetailsWrapper.saveCombinationFailed', defaultMessage: 'Failed to save changes' },
    description: {
        id: 'clu.cluEntityDetailsWrapper.description',
        defaultMessage: `Each entity is made of multiple components that you can define. You can define one or multiple components. If you have more than one component,
        the logic that combines their responses is defined in the Overlap method tab.`
    }
});

const { getClassName } = getStylistV2('CluEntityDetailsWrapper');
const pivotItemClassName = getClassName('PivotItem', { height: '100%' });
const pivotContainerStyles: React.CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column' };

const pivotStyles = classNamesFunction<IPivotStyleProps, IPivotStyles>()({ root: { marginBottom: 15 }, itemContainer: { flex: 1 } });

/**
 * Component displaying the entity capabilities and combination options
 */
export const CluEntityDetailsWrapper = observer((props: CluEntityDetailsWrapperProps) => {
    const { entityName } = props;
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const defaultOperationsTracker = useProgressTracker();

    const overlapButtonIdRef = React.useRef<string>(getId());

    const entityStore = rootStore.currentCluProjectStore.entityStoreHouse.findById(entityName);

    const [selectedCombinationOption, setSelectedCombinationOption] = React.useState<EntityCompositionSetting>(
        entityStore.compositionSetting
    );

    const [selectedPivotKey, setSelectedPivotKey] = React.useState<string>(localize(messages.entityComponents));

    const handleOnSaveCompositionSetting = (compositionSetting: EntityCompositionSetting) => {
        setSelectedCombinationOption(compositionSetting);
        dispatcher.dispatch('updateCluEntityCombinationOption', {
            name: entityStore.name,
            compositionSetting,
            tracker: defaultOperationsTracker,
            toastData: { failed: { title: localize(messages.saveCombinationFailed) } }
        });
    };

    return (
        <Stack verticalFill tokens={{ childrenGap: 24 }}>
            <Stack tokens={{ childrenGap: 4 }}>
                <EditableTitle title={entityName} />
                <LuisText>{localize(messages.description)}</LuisText>
            </Stack>

            <Pivot
                linkSize="large"
                styles={pivotStyles}
                style={pivotContainerStyles}
                selectedKey={selectedPivotKey}
                onLinkClick={item => setSelectedPivotKey(item.props.itemKey)}
                getTabId={key => (key === localize(messages.entityOptions) ? overlapButtonIdRef.current : null)}>
                <PivotItem
                    className={pivotItemClassName}
                    itemKey={localize(messages.entityComponents)}
                    headerText={localize(messages.entityComponents)}>
                    <CluEntityDetailsPivot overlapButtonId={overlapButtonIdRef.current} entityStore={entityStore} />
                </PivotItem>

                <PivotItem
                    className={pivotItemClassName}
                    itemKey={localize(messages.entityOptions)}
                    headerText={localize(messages.entityOptions)}>
                    <CluEntityCombinationPivot
                        entityStore={entityStore}
                        selectedCombinationOption={selectedCombinationOption}
                        isSaveOperationRunning={defaultOperationsTracker.isRunning}
                        setSelectedCombinationOption={handleOnSaveCompositionSetting}
                        onEntityComponentClick={() => setSelectedPivotKey(localize(messages.entityComponents))}
                    />
                </PivotItem>
            </Pivot>
        </Stack>
    );
});
